import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ChatUiSessionState, ChatUiState, initialChatUiState } from './chatUiState';
import { appendMessages } from '../chatMessages/chatMessagesSlice';

export interface UpdateDraftInputPayload {
    sessionId: string;
    value: string;
}

export interface SetSelectedTabPayload {
    sessionId: string;
    selectedTab: 'chat' | 'documents' | 'persona' | 'overview' | 'inputs' | 'ea-chat';
}

export interface SetSelectedAgentsPayload {
    sessionId: string;
    agentIds: string[];
}

export interface ToggleSelectedAgentPayload {
    sessionId: string;
    agentId: string;
}

export interface SetIsLoadingMessagesPayload {
    sessionId: string;
    value: boolean;
}

export interface SetIsBulkUploadProcessingPayload {
    sessionId: string;
    value: boolean;
    fileName?: string;
}

export interface SetThinkLongerPayload {
    sessionId: string;
    value: boolean;
}

export interface SetHasMoreMessagesPayload {
	sessionId: string;
	value: boolean;
}

export interface SetLoadedMessageCountPayload {
	sessionId: string;
	value: number;
}


const ensureSessionUiStateInternal = (state: ChatUiState, sessionId: string): ChatUiSessionState => {
    // if we don't have any state for this sessionId (first time selecting it, on load, etc.), initialize it
    if (!state.bySessionId[sessionId]) {
        state.bySessionId[sessionId] = {};
    }

    return state.bySessionId[sessionId];
};

const chatUiSlice = createSlice({
    name: 'chatUi',
    initialState: initialChatUiState,
    reducers: {
        setSelectedSessionId: (state: ChatUiState, action: PayloadAction<string | null>) => {
            state.selectedSessionId = action.payload;

            // switch back to chat tab whenever session changes. THIS WILL NEED TO BE CHANGED FOR PKA I THINK
            if (action.payload) {
                const sessionUiState = ensureSessionUiStateInternal(state, action.payload);
                sessionUiState.selectedTab = 'chat';
            }
        },
        setSelectedTab: (state: ChatUiState, action: PayloadAction<SetSelectedTabPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.selectedTab = action.payload.selectedTab;
        },
        updateDraftInput: (state: ChatUiState, action: PayloadAction<UpdateDraftInputPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.draftInput = action.payload.value;
        },
        setSelectedAgents: (state: ChatUiState, action: PayloadAction<SetSelectedAgentsPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.selectedAgents = [...action.payload.agentIds];
        },
        toggleSelectedAgent: (state: ChatUiState, action: PayloadAction<ToggleSelectedAgentPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            const selectedAgents = sessionUiState.selectedAgents ?? [];
            const nextSelectedAgents = selectedAgents.includes(action.payload.agentId)
                ? selectedAgents.filter((agentId) => agentId !== action.payload.agentId)
                : [...selectedAgents, action.payload.agentId];

            sessionUiState.selectedAgents = nextSelectedAgents;
        },
        setIsLoadingMessages: (state: ChatUiState, action: PayloadAction<SetIsLoadingMessagesPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.isLoadingMessages = action.payload.value;
        },
        setIsBulkUploadProcessing: (state: ChatUiState, action: PayloadAction<SetIsBulkUploadProcessingPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.isBulkUploadProcessing = action.payload.value;

            if (action.payload.fileName !== undefined) {
                sessionUiState.bulkUploadFileName = action.payload.fileName;
            }

            if (!action.payload.value) {
                sessionUiState.bulkUploadFileName = undefined;
                sessionUiState.bulkStatusMessageId = undefined;
            }
        },
        setThinkLonger: (state: ChatUiState, action: PayloadAction<SetThinkLongerPayload>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.thinkLonger = action.payload.value;
        },

        setIsResponseGenerating: (
            state: ChatUiState,
            action: PayloadAction<{
                sessionId: string;
                value: boolean;
                fallbackContent?: string;
                wasAborted?: boolean;
            }>,
        ) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.isResponseGenerating = action.payload.value;
        },
        setIsTitleGenerating: (state: ChatUiState, action: PayloadAction<{ sessionId: string; value: boolean }>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.isTitleGenerating = action.payload.value;
        },
        setStreamingMessageId: (
            state: ChatUiState,
            action: PayloadAction<{ sessionId: string; value: string | null }>,
        ) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.streamingMessageId = action.payload.value ?? undefined;
        },
        setBulkStatusMessageId: (
            state: ChatUiState,
            action: PayloadAction<{ sessionId: string; value: string | null }>,
        ) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.bulkStatusMessageId = action.payload.value ?? undefined;
        },
        setBotResponseStatus: (state: ChatUiState, action: PayloadAction<{ sessionId: string; value: string }>) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.botResponseStatus = action.payload.value;
        },
        addPptxExportingMessageId: (
            state: ChatUiState,
            action: PayloadAction<{ sessionId: string; messageId: string }>,
        ) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            const ids = sessionUiState.pptxExportingMessageIds ?? [];
            if (!ids.includes(action.payload.messageId)) {
                sessionUiState.pptxExportingMessageIds = [...ids, action.payload.messageId];
            }
        },
        removePptxExportingMessageId: (
            state: ChatUiState,
            action: PayloadAction<{ sessionId: string; messageId: string }>,
        ) => {
            const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
            sessionUiState.pptxExportingMessageIds = (sessionUiState.pptxExportingMessageIds ?? []).filter(
                (id) => id !== action.payload.messageId,
            );
        },
		setHasMoreMessages: (state: ChatUiState, action: PayloadAction<SetHasMoreMessagesPayload>) => {
			const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
			sessionUiState.hasMoreMessages = action.payload.value;
		},
		setLoadedMessageCount: (state: ChatUiState, action: PayloadAction<SetLoadedMessageCountPayload>) => {
			const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
			sessionUiState.loadedMessageCount = action.payload.value;
		},
        clearChatUiState: (state: ChatUiState) => {
            state.selectedSessionId = null;
            state.bySessionId = {};
            state.canvasOpen = false;
        },
     },
	extraReducers: (builder) => {
		// Keep loadedMessageCount in sync when new messages are appended (user sends / bot responds).
		// Without this, the skip cursor used in loadMoreMessages stays stale and eventually
		// overlaps with newly sent messages, causing duplicates when paginating old history.
		builder.addCase(appendMessages, (state, action) => {
			const sessionUiState = ensureSessionUiStateInternal(state, action.payload.sessionId);
			sessionUiState.loadedMessageCount = (sessionUiState.loadedMessageCount ?? 0) + action.payload.messages.length;
		});
    },
});

export const {
    setSelectedSessionId,
    setSelectedTab,
    updateDraftInput,
    setSelectedAgents,
    toggleSelectedAgent,
    setIsLoadingMessages,
    setIsBulkUploadProcessing,
    setThinkLonger,
    setIsTitleGenerating,
    setIsResponseGenerating,
    setStreamingMessageId,
    setBulkStatusMessageId,
    setBotResponseStatus,
    addPptxExportingMessageId,
    removePptxExportingMessageId,    
    setHasMoreMessages,
    setLoadedMessageCount,
    clearChatUiState,
} = chatUiSlice.actions;

export default chatUiSlice.reducer;
